/**
 * buildHandleQuery · Builder for the query orchestrator's core dispatch.
 * Byte-verbatim extraction of the 460-line handleQuery function from
 * KnowledgeGraphPage.jsx (pre-extraction lines 1357-1816). Zero behaviour
 * change. Every axios URL · toast copy · conversation-history mapping ·
 * polling loop · intent-detection branch preserved.
 *
 * Extracted 2026-08-01 · IJ-246 · Phase 5D-2 of the KnowledgeGraphPage.jsx
 * split. Umbrella: IJ-076. Owner: E1.
 *
 * Pattern: buildHandleQuery(deps) returns the async handler. Deps are
 * destructured at the top so the body reads and writes identically to the
 * original inline code (React inversion-of-control for testable logic).
 * Deps signature is deliberately over-broad · unused keys are ignored.
 */
import { API } from '../../utils/authClient';
import { toast } from 'sonner';
import { trackEvent } from '../../utils/telemetry';
import { classifyIntent } from '../../lib/queryIntent';
import {
  classifyCommand as classifyQuietCommand,
  setQuietMode as persistQuietMode,
  hasSeenFirstTimeTip as hasSeenQuietTip,
  markFirstTimeTipSeen as markQuietTipSeen,
  COPY as QUIET_COPY,
} from '../../lib/quietMode';

export function buildHandleQuery(deps) {
  const {
    query, queryThread, quietMode, dataControls, selectedSources, queryIntent,
    detectedSubject, firstTimeTip, followUpQuery, showSourceOptions,
    userLocation,
    setQueryThread, setIsQuerying, setQuery, setQueryIntent,
    setDetectedSubject, setFollowUpQuery, setAwaitingForecastFile,
    setQuietModeState, setSelectedSources, setShowSourceOptions,
    followUpRef, queryTextareaRef, handleQueryRef,
    handleQuotaExceededError, handleExportThread, handleProductSearch,
    attemptProductSearch, incrementBilledCount, logSearchActivity,
    logLocationActivity, scrollToThreadItem, requestGeolocation,
    quietOnToast, quietOffToast,
  } = deps;

  const handleQuery = async (queryText, isFollowUp = false, refinementFacets = null) => {
    const q = queryText || (isFollowUp ? followUpQuery : query);
    if (!q.trim()) { toast.error('Please enter a query'); return; }

    // Lever 6 (Jun 3, 2026) — quiet-mode typed commands.
    // Full-string match on lowered+trimmed query string. Real queries
    // like "Economic Forecast 2026. Add Detail" do NOT trigger because
    // they contain extra characters beyond the bare command phrase.
    const quietCmd = classifyQuietCommand(q);
    if (quietCmd === 'quiet_on') {
      setQuietModeState(true);
      persistQuietMode(true);
      toast.success(QUIET_COPY.quietOnToast, { duration: 4500 });
      if (isFollowUp) setFollowUpQuery(''); else setQuery('');
      return;
    }
    if (quietCmd === 'quiet_off') {
      setQuietModeState(false);
      persistQuietMode(false);
      toast.success(QUIET_COPY.quietOffToast, { duration: 4500 });
      if (isFollowUp) setFollowUpQuery(''); else setQuery('');
      return;
    }

    // SPD v5.71 — sanitised query_submitted telemetry. No raw query text is
    // ever sent; we send only length bucket, word count, and follow-up /
    // refinement flags so the admin dashboard can measure query volume per
    // user without leaking PII or sensitive content.
    //
    // SPD v5.71-fix (2026-02-11) — All property values MUST be strings.
    // Backend schema is `Dict[str, str]` and utils/telemetry.js strips any
    // non-string value. Coerce here at the call site so the four spec
    // fields survive both filters. Iteration 64 caught this.
    try {
      const qLen = q.length;
      const bucket = qLen < 30 ? 'short' : qLen <= 100 ? 'medium' : 'long';
      const wordCount = q.trim().split(/\s+/).filter(Boolean).length;
      trackEvent('query_submitted', {
        length_bucket: bucket,
        word_count: String(wordCount),
        is_follow_up: isFollowUp ? 'true' : 'false',
        has_refinement: refinementFacets ? 'true' : 'false',
      });
    } catch (_) { /* telemetry must never block a query */ }

    // Intercept "export entire thread" command
    if (q.toLowerCase().includes('export') && (q.toLowerCase().includes('thread') || q.toLowerCase().includes('entire'))) {
      handleExportThread();
      if (isFollowUp) setFollowUpQuery('');
      // Keep query text in the input box
      return;
    }

    // Intercept financial forecast queries — don't send to knowledge graph.
    // SPD v5.76 (2026-07-13) — D2-followup fix. Previously the intercept
    // used a loose `.includes('forecast')` match which also caught queries
    // like "economic forecast", "weather forecast", "market forecast" and
    // shunted them into the P&L upload flow. Now the intercept only fires
    // when the query has a genuine P&L / company-finance shape:
    //   (a) direct P&L / profit-and-loss mention, OR
    //   (b) a forecast/projection/budget word AND a company-finance marker
    //       (revenue, expenses, EBITDA, profit, earnings, etc.).
    // Anything else — including "economic forecast for Canada" — falls
    // through to the normal query path.
    const qLower = q.toLowerCase();
    const RE_DIRECT_PNL = /\bp&l\b|\bpnl\b|\bp and l\b|\bprofit\s+(?:and|&)\s+loss\b/;
    const RE_FORECAST_VERB = /\b(?:forecast|forecasts?|forecasting|projection|projections?|budget)\b/;
    const RE_FINANCE_MARKER = /\b(?:revenue|revenues|expense|expenses|cost of goods|cogs|ebitda|opex|capex|gross margin|net income|earnings|cash flow|p&l|pnl|profit|loss|company|business)\b/;
    const isForecastQuery = (
      RE_DIRECT_PNL.test(qLower) ||
      (RE_FORECAST_VERB.test(qLower) && RE_FINANCE_MARKER.test(qLower))
    );
    if (isForecastQuery) {
      setAwaitingForecastFile(true);
      const forecastIdx = queryThread.length;
      setQueryThread(prev => [...prev, {
        query: q,
        result: {
          answer: '**Ready to generate your P&L forecast.**\n\nPlease upload your Excel budget file (.xlsx) below, then click **"Generate Forecast"**.',
          source_entities: [],
          source_documents: [],
          related_concepts: [],
          images: [],
          suggestions: [],
          version: '',
          type: 'forecast_prompt',
        }
      }]);
      if (isFollowUp) setFollowUpQuery('');
      // Keep query text in the input box
      scrollToThreadItem(forecastIdx);
      return;
    }
    
    // Handle product/service queries with selected sources (triggered via Enter or magnifier)
    if (selectedSources.length > 0 && showSourceOptions) {
      const sources = selectedSources.includes('compare')
        ? ['local', 'online', 'compare']
        : selectedSources;
      // Route through the clarification gate (T5) instead of calling
      // handleProductSearch directly — vague queries hit the modal.
      attemptProductSearch(q, sources);
      if (isFollowUp) setFollowUpQuery('');
      return;
    }
    
    setIsQuerying(true);
    
    // Build conversation history from thread (include source_documents for enrichment)
    const conversation_history = queryThread.map(item => ({
      query: item.query,
      answer: item.result.answer,
      source_documents: item.result.source_documents || []
    }));
    
    // Add placeholder to thread immediately with loading state
    const threadIdx = queryThread.length;
    // SPD v5.86 hotfix (2026-02-14) · operator UX critique — the
    // informational-query loading state was rendering the same small
    // italic text-in-the-middle-of-a-huge-grey-box that the product-
    // search state used to have. Adding an explicit `type: 'general'`
    // marker + `progressMessage` here so the same big-centered-black
    // loading overlay used by product_search renders for informational
    // queries too. The overlay is gated on isStreaming + type below.
    const loadingResult = {
      type: 'general',
      progressMessage: 'Searching knowledge graph and web sources',
      answer: '*Searching knowledge graph and web sources...*',
      source_entities: [],
      source_documents: [],
      related_concepts: [],
      images: [],
      suggestions: [],
      version: '',
      citation_map: {},
      web_results: [],
      web_intro: '',
      has_kg_content: false
    };
    setQueryThread(prev => [...prev, { query: q, result: loadingResult, isStreaming: true, refinement_facets: refinementFacets || null }]);
    // Scroll to the NEW item immediately so the user sees their own query's
    // loading spinner — not the previous query's result.
    scrollToThreadItem(threadIdx);
    
    try {
      const token = localStorage.getItem('startech_token');
      
      // Phase 5: If Location is ON, ensure we have geolocation data
      let locationData = userLocation;
      if (dataControls.location === 'on' && !locationData) {
        locationData = await requestGeolocation();
      }
      
      // Step 1: Start query as background job (returns immediately)
      const queryBody = { query: q, query_type: 'general', conversation_history, quiet_mode: quietMode };
      if (refinementFacets && Object.keys(refinementFacets).length > 0) {
        // D4 (Jun 15 2026) — chip selections travel as STRUCTURED METADATA,
        // not appended to the query text. Preserves Brave/LLM latency budget.
        queryBody.refinement_facets = refinementFacets;
      }
      if (locationData && locationData.lat) {
        queryBody.user_location = {
          lat: locationData.lat,
          lng: locationData.lng,
          suburb: locationData.suburb || '',
          city: locationData.city || '',
          region: locationData.region || '',
          display: locationData.display || '',
        };
      }
      
      const startResp = await fetch(`${API}/query/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(queryBody)
      });
      
      if (!startResp.ok) {
        // Beta 3-query quota — server returns 402 with structured detail.
        // Handle inline (rather than throwing) so the Exit Banner fires
        // cleanly without surfacing as a generic error toast.
        if (startResp.status === 402) {
          let qErr = {};
          try { qErr = await startResp.json(); } catch {}
          handleQuotaExceededError({
            response: { status: 402, data: qErr },
          });
          setIsQuerying(false);
          return;
        }
        let detail = '';
        try { const err = await startResp.json(); detail = err.detail || ''; } catch {}
        throw new Error(detail || `Failed to start query (${startResp.status})`);
      }
      
      const startData = await startResp.json();
      
      // Phase 5: Handle permission_required — agent asks clarification question
      if (startData.status === 'permission_required') {
        const permsNeeded = startData.permissions_needed || [];
        const clarification = startData.clarification || 'This query requires additional permissions.';
        const permLabels = permsNeeded.map(p => p.label).join(', ');
        
        // Show the clarification as an agent response
        const clarificationResult = {
          answer: clarification,
          source_entities: [],
          source_documents: [],
          related_concepts: [],
          images: [],
          suggestions: [],
          version: '',
          citation_map: {},
          web_results: [],
          web_intro: '',
          has_kg_content: false,
          type: 'permission_required',
          permissions_needed: permsNeeded,
          original_query: q,
          kg_relevant: startData.kg_relevant,
        };
        
        setQueryThread(prev => {
          const updated = [...prev];
          updated[threadIdx] = { query: q, result: clarificationResult, isStreaming: false };
          return updated;
        });
        setIsQuerying(false);
        toast.info(`${permLabels} required for this query`);
        return;
      }
      
      // Handle clarification_needed — agent needs more info from user
      if (startData.status === 'clarification_needed') {
        const clarificationResult = {
          answer: startData.clarification || 'Could you be more specific about what you\'re looking for?',
          source_entities: [], source_documents: [], related_concepts: [],
          images: [], suggestions: [], version: '', citation_map: {},
          web_results: [], web_intro: '', has_kg_content: false,
          type: 'clarification',
          original_query: q,
        };
        setQueryThread(prev => {
          const updated = [...prev];
          updated[threadIdx] = { query: q, result: clarificationResult, isStreaming: false };
          return updated;
        });
        setIsQuerying(false);
        toast.info('The agent needs more context');
        return;
      }
      
      const { job_id } = startData;
      
      // Step 2: Poll for results with progress feedback
      let data = null;
      const maxPollTime = 300000; // 5 minute max (allows for LLM retries)
      const pollInterval = 3000;
      const startTime = Date.now();
      let consecutiveFailures = 0;
      
      while (Date.now() - startTime < maxPollTime) {
        await new Promise(r => setTimeout(r, pollInterval));
        
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        
        // Update the thread entry with progress info
        setQueryThread(prev => {
          const updated = [...prev];
          if (updated[threadIdx]) {
            updated[threadIdx] = { 
              ...updated[threadIdx], 
              progressSeconds: elapsed,
              progressMessage: consecutiveFailures > 2 
                ? 'Connection unstable, still trying...' 
                : elapsed > 30 
                  ? elapsed > 90 
                    ? 'Still processing... AI service may be busy, please wait'
                    : 'Still processing... complex queries can take 1-2 minutes'
                  : null
            };
          }
          return updated;
        });
        
        try {
          const pollResp = await fetch(`${API}/query/result/${job_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (!pollResp.ok) {
            consecutiveFailures++;
            if (consecutiveFailures > 10) {
              throw new Error('Unable to reach the server. Please check your connection and try again.');
            }
            continue;
          }
          
          consecutiveFailures = 0; // Reset on success
          const pollData = await pollResp.json();
          
          if (pollData.status === 'complete') {
            data = pollData.result;
            data._coordinator = pollData.coordinator || null;
            break;
          } else if (pollData.status === 'error') {
            throw new Error(pollData.error || 'Query processing failed');
          } else if (pollData.status === 'permission_required') {
            // Layer 1 refactor (Feb 24, 2026): the Permission Gate now runs
            // in the background after /query/start has returned, so this
            // status arrives here via poll rather than from the initial
            // /query/start response. The handler mirrors the startData
            // handler at the top of submitQuery exactly.
            const permsNeeded = pollData.permissions_needed || [];
            const clarification = pollData.clarification || 'This query requires additional permissions.';
            const permLabels = permsNeeded.map(p => p.label).join(', ');
            const clarificationResult = {
              answer: clarification,
              source_entities: [], source_documents: [], related_concepts: [],
              images: [], suggestions: [], version: '', citation_map: {},
              web_results: [], web_intro: '', has_kg_content: false,
              type: 'permission_required',
              permissions_needed: permsNeeded,
              original_query: q,
              kg_relevant: pollData.kg_relevant,
            };
            setQueryThread(prev => {
              const updated = [...prev];
              updated[threadIdx] = { query: q, result: clarificationResult, isStreaming: false };
              return updated;
            });
            setIsQuerying(false);
            toast.info(`${permLabels} required for this query`);
            return;
          } else if (pollData.status === 'clarification_needed') {
            const clarificationResult = {
              answer: pollData.clarification || 'Could you be more specific about what you\'re looking for?',
              source_entities: [], source_documents: [], related_concepts: [],
              images: [], suggestions: [], version: '', citation_map: {},
              web_results: [], web_intro: '', has_kg_content: false,
              type: 'clarification',
              original_query: q,
            };
            setQueryThread(prev => {
              const updated = [...prev];
              updated[threadIdx] = { query: q, result: clarificationResult, isStreaming: false };
              return updated;
            });
            setIsQuerying(false);
            toast.info('The agent needs more context');
            return;
          }
        } catch (pollError) {
          if (pollError.message.includes('Unable to reach') || pollError.message.includes('processing failed') || pollError.message.includes('Budget')) {
            throw pollError;
          }
          // Network error during fetch - count as failure
          consecutiveFailures++;
          if (consecutiveFailures > 10) {
            throw new Error('Connection lost. Please refresh and try again.');
          }
          continue;
        }
      }
      
      if (!data) {
        throw new Error('Query timed out. The AI service may be experiencing high demand. Please try again in a moment.');
      }
      
      // Update thread with complete result
      const _coord = data._coordinator || null;
      const completeResult = {
        answer: data.answer || '',
        source_entities: data.source_entities || [],
        source_documents: data.source_documents || [],
        related_concepts: data.related_concepts || [],
        images: data.images || [],
        suggestions: data.suggestions || [],
        version: data.version || '',
        citation_map: data.citation_map || {},
        web_results: data.web_results || [],
        web_intro: data.web_intro || '',
        has_kg_content: data.has_kg_content || false,
        clarifying_question: (_coord && _coord.needs_clarification) ? (_coord.clarifying_question || '') : '',
      };
      
      setQueryThread(prev => {
        const updated = [...prev];
        updated[threadIdx] = { query: q, result: completeResult, isStreaming: false, query_id: job_id, refinement_facets: refinementFacets || null };
        return updated;
      });
      
      // Log search activity for data tracking (if user opted in)
      logSearchActivity(q);
      logLocationActivity();
      
      if (isFollowUp) setFollowUpQuery('');
      toast.success('Query completed');

      // Lever 6 (Jun 3, 2026) — first-time quiet-mode tip.
      // Fires once, AFTER the user has experienced a verbose answer.
      // Self-disables via localStorage; never shown to admin/test harness twice.
      if (!hasSeenQuietTip()) {
        markQuietTipSeen();
        // Slight delay so the success toast and the tip do not stack on top
        // of each other in the same animation tick.
        setTimeout(() => {
          toast.info(QUIET_COPY.firstTimeTip, { duration: 8000 });
        }, 1200);
      }
      // Sticky billable counter — only real (non-demo) queries count
      // toward the 3-query quota.
      incrementBilledCount();
      scrollToThreadItem(threadIdx);
      // D7 fix (Feb 28, 2026): after a successful submit, move focus to
      // the follow-up "Search-it" input so the user's next keystrokes
      // land there — NOT back in the primary query field above. Defer
      // one tick so the follow-up input is mounted before we focus it.
      setTimeout(() => {
        if (followUpRef.current) {
          try { followUpRef.current.focus({ preventScroll: true }); }
          catch { followUpRef.current.focus(); }
        } else if (queryTextareaRef.current) {
          // Blur the primary field so the global keydown router can
          // catch the next keystroke and route it correctly.
          queryTextareaRef.current.blur();
        }
      }, 200);
    } catch (error) {
      console.error('Query error:', error);
      const errorMsg = error.message || 'Failed to process query';
      let userMessage;
      
      if (errorMsg.includes('Budget')) {
        userMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (errorMsg.includes('502') || errorMsg.includes('503') || errorMsg.includes('unavailable')) {
        userMessage = 'AI service is busy. Please wait a moment and try again.';
      } else if (errorMsg.includes('500') || errorMsg.includes('Server error')) {
        userMessage = 'Server error processing query. Please try again.';
      } else if (errorMsg.includes('timed out') || errorMsg.includes('timeout')) {
        userMessage = 'Query took too long. Please try a simpler query.';
      } else if (errorMsg.includes('Connection') || errorMsg.includes('network')) {
        userMessage = 'Connection issue. Please check your internet and try again.';
      } else {
        userMessage = errorMsg;
      }
      
      toast.error(userMessage);
      // Remove the failed entry
      setQueryThread(prev => prev.filter((_, idx) => idx !== threadIdx));
    } finally {
      setIsQuerying(false);
    }
  };

  // Update ref for voice command access
  handleQueryRef.current = handleQuery;

  return handleQuery;
}

export default buildHandleQuery;
